import saleCampaigns from '../config/saleCampaigns.json';
import { convertUnitToSubUnit, unitDivisor } from './currency';
import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

const isPositiveNumber = value => Number.isFinite(value) && value > 0;

const getValidMajorUnitPrice = value => {
  const price = Number(value);
  return isPositiveNumber(price) ? price : null;
};

const getMoneyAmount = money => {
  const amount = money?.amount;
  return typeof amount === 'object' && amount?.toString
    ? Number(amount.toString())
    : Number(amount);
};

export const getSaleCampaign = campaignKey => saleCampaigns[campaignKey] || null;

export const getSaleCampaignKeys = () => Object.keys(saleCampaigns);

export const getCampaignListingPrices = campaign => {
  const listingIds = campaign?.listingIds || {};
  const prices = {};

  if (Array.isArray(listingIds)) {
    return listingIds.reduce((prices, item) => {
      const listingId = item?.listingId || item?.id;
      const discountedPrice = item?.discountedPrice || item?.price;
      const validPrice = getValidMajorUnitPrice(discountedPrice);
      if (listingId && validPrice) {
        prices[listingId] = validPrice;
      }
      return prices;
    }, prices);
  }

  return Object.entries(listingIds).reduce((prices, [listingId, value]) => {
    const discountedPrice =
      typeof value === 'number' ? value : value?.discountedPrice || value?.price;
    const validPrice = getValidMajorUnitPrice(discountedPrice);
    if (validPrice) {
      prices[listingId] = validPrice;
    }
    return prices;
  }, prices);
};

export const getCampaignListingIds = campaign => {
  const listingIds = campaign?.listingIds || {};

  if (Array.isArray(listingIds)) {
    return listingIds.reduce((ids, item) => {
      const listingId = item?.listingId || item?.id;
      return listingId ? [...ids, listingId] : ids;
    }, []);
  }

  return Object.keys(listingIds);
};

export const getSaleListingIds = campaignKey => getCampaignListingIds(getSaleCampaign(campaignKey));

const salePriceByListingId = Object.entries(saleCampaigns).reduce(
  (lookup, [campaignKey, campaign]) => {
    const campaignPrices = getCampaignListingPrices(campaign);

    Object.entries(campaignPrices).forEach(([listingId, discountedPrice]) => {
      const currentPrice = lookup.lowestPrice[listingId];
      if (currentPrice == null || discountedPrice < currentPrice) {
        lookup.lowestPrice[listingId] = discountedPrice;
        lookup.lowestSale[listingId] = { campaignKey, discountedPrice };
      }
    });

    return lookup;
  },
  { lowestPrice: {}, lowestSale: {} }
);

export const getLowestSalePrice = listingId => {
  const id = listingId?.uuid || listingId;
  return id ? salePriceByListingId.lowestPrice[id] || null : null;
};

export const getLowestSaleCampaignForListing = listingId => {
  const id = listingId?.uuid || listingId;
  return id ? salePriceByListingId.lowestSale[id] || null : null;
};

export const isEligibleSalePrice = (discountedPriceSubunit, originalAmount) =>
  Number.isFinite(discountedPriceSubunit) &&
  discountedPriceSubunit > 0 &&
  Number.isFinite(originalAmount) &&
  discountedPriceSubunit < originalAmount;

export const getSalePriceFromMajorUnit = (listing, discountedPrice, marketplaceCurrency) => {
  const originalPrice = listing?.attributes?.price;
  const originalAmount = getMoneyAmount(originalPrice);
  const price = getValidMajorUnitPrice(discountedPrice);

  if (!originalPrice || !isPositiveNumber(price) || !originalPrice.currency) {
    return null;
  }

  const currency = originalPrice.currency || marketplaceCurrency;
  let discountedPriceSubunit;
  try {
    discountedPriceSubunit = convertUnitToSubUnit(price, unitDivisor(currency));
  } catch (e) {
    return null;
  }

  if (!isEligibleSalePrice(discountedPriceSubunit, originalAmount)) {
    return null;
  }

  return new Money(discountedPriceSubunit, currency);
};

export const getSalePriceForListing = (listing, marketplaceCurrency) => {
  const listingId = listing?.id?.uuid;
  const discountedPrice = getLowestSalePrice(listingId);
  return getSalePriceFromMajorUnit(listing, discountedPrice, marketplaceCurrency);
};

export default saleCampaigns;
